import { useEffect, useState } from "react";
import "../../../styles/DashboardStyles/DashboardContent/DashboardUsers.css";
import { db, auth } from '../../../firebase/config';
import { collection, onSnapshot, getDoc, doc, deleteDoc } from "firebase/firestore";
import UserProfileModal from '../../../components/DashboardSection/Overlays/UserProfileModal';
import ResetPasswordModal from '../../../components/DashboardSection/Overlays/ResetPasswordModal';

type Status = "active" | "pending" | "rejected" | "suspended";
type SortDir = "asc" | "desc" | null;

interface User {
   id: string;        // Firestore document ID (userId)
  fullName: string;  // matches your Firestore field
  email: string;
  phone: string;
  role: string;
  accountStatus: Status; // matches your Firestore field
  createdAt: any;
  updatedAt: any;
  userId: string;
}

export default function DashboardUsers() {
  // Add this state inside your component
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roleSort, setRoleSort] = useState<SortDir>(null);
  const [statusSort, setStatusSort] = useState<SortDir>(null);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activeModal, setActiveModal] = useState<"profile" | "reset" | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const container = document.querySelector(".dashboardUsers" ) as HTMLElement;


  const handleScroll = () => setScrolled(container?.scrollTop > 10);

  
 container?.addEventListener("scroll", handleScroll);
  return () => container?.removeEventListener("scroll", handleScroll);
}, []);

useEffect(() => {
    // onSnapshot listens in real-time — table updates instantly
    // when any user document changes in Firestore
    const unsubscribe = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as User[];

        setUsers(data);
        setLoading(false);
      },
      (err) => {
        console.error("Here is the error of why user names are not loading: ",err);
        setError("Failed to load users.");
        setLoading(false);
      }
    );

    // Cleanup — stops listening when component unmounts
    return () => unsubscribe();
  }, []); // ← empty array means this runs once on mount

  useEffect(() => {
  const fetchRole = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const snap = await getDoc(doc(db, "users", currentUser.uid));
      if (snap.exists()) {
        setCurrentUserRole(snap.data().role);
      }
    }
  };
  fetchRole();
}, []);

  // ── Search filter ──
  const filtered = users.filter(u =>
    u.fullName.toLowerCase().includes(search.toLowerCase())
  );

  // ── Sort helpers ──
  const toggleSort = (col: "role" | "accountStatus") => {
    if (col === "role") {
      const next: SortDir = roleSort === "asc" ? "desc" : "asc";
      setRoleSort(next);
      setStatusSort(null);
      setUsers(prev => [...prev].sort((a, b) =>
        next === "asc"
          ? a.role.localeCompare(b.role)
          : b.role.localeCompare(a.role)
      ));
    } else {
      const next: SortDir = statusSort === "asc" ? "desc" : "asc";
      setStatusSort(next);
      setRoleSort(null);
      setUsers(prev => [...prev].sort((a, b) =>
        next === "asc"
          ? a.accountStatus.localeCompare(b.accountStatus)
          : b.accountStatus.localeCompare(a.accountStatus)
      ));
    }
  };

  // ── Delete ──
  const handleDelete = async (id: string) => {
  if (window.confirm("Are you sure you want to delete this user?")) {
    try {
      // 1. Delete from Firestore first
      await deleteDoc(doc(db, "users", id));

      // 2. Then remove from local state
      setUsers(prev => prev.filter(u => u.id !== id));

    } catch (error) {
      console.error("Failed to delete user:", error);
      alert("Something went wrong. User was not deleted.");
    }
  }
};

  // Add this handler
 const handleUserDeleted = async (id: string) => {
  try {
    // 1. Delete from Firestore first
    await deleteDoc(doc(db, "users", id));

    // 2. Then remove from local state
    setUsers(prev => prev.filter(u => u.id !== id));

  } catch (error) {
    console.error("Failed to delete user:", error);
    alert("Something went wrong. User was not deleted.");
  }
};

  return (
    <div className="dashboardUsers">
      <h2 className="title">Dashboard Users</h2>
      {/* HEADER */}
      <div className={`header ${scrolled ? "header--floating" : ""}`}>
        <input
          type="text"
          name="search"
          placeholder="Search applicant name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        
      </div>

     {/* TABLE */}
    <table>
    <thead>
        <tr>
        <th>Name</th>
        <th>Email</th>
        {/* Sortable: Role */}
        <th
            className={`sortable${roleSort ? ` sort-${roleSort}` : ""}`}
            onClick={() => toggleSort("role")}
        >
            Role
        </th>
        <th
            className={`sortable${statusSort ? ` sort-${statusSort}` : ""}`}
            onClick={() => toggleSort("accountStatus")}
        >
            Status
        </th>
        <th>Actions</th>
        </tr>
    </thead>
    <tbody>
        {loading ? (
        <tr>
            <td colSpan={5} style={{ textAlign: "center", padding: "var(--spacing-lg)" }}>
            Loading users...
            </td>
        </tr>
        ) : error ? (
        <tr>
            <td colSpan={5} style={{ textAlign: "center", color: "var(--color-danger)" }}>
            {error}
            </td>
        </tr>
        ) : filtered.map(user => (
        <tr key={user.id}>
            {/* user.fullName */}
            <td>{user.fullName}</td>
            <td>{user.email}</td>
            <td>{user.role}</td>
            <td>
            <span className={`status-badge ${user.accountStatus}`}>
                {user.accountStatus}
            </span>
            </td>
            <td>
            <button
              className="btn-view"
              onClick={() => {
                setSelectedUser(user);
                setActiveModal("profile");
              }}
              >
              View Profile
            </button>
            <button
              className="btn-reset"
              onClick={() => {
                setSelectedUser(user);
                setActiveModal("reset");
              }}
            >
              Reset Password
            </button>
            {currentUserRole === "superadmin" && (
            <button className="btn-delete" onClick={() => handleDelete(user.id)}>
                Delete
            </button>
            )}
            </td>
        </tr>
        ))}
    </tbody>
    </table>

      {selectedUser && activeModal === "profile" && (
      <UserProfileModal
        user={selectedUser}
        onClose={() => {
          setSelectedUser(null);
          setActiveModal(null);
        }}
        onDeleted={handleUserDeleted}
        currentUserRole={currentUserRole}
      />
    )}

     {selectedUser && activeModal === "reset" && (
      <ResetPasswordModal
        user={selectedUser}
        onClose={() => {
          setSelectedUser(null);
          setActiveModal(null);
        }}
      />
    )}

    </div>
  );
}